import { Injectable, Injector, OnDestroy } from '@angular/core';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { WorkOrderDetailsDto } from '@app/generated/model';
import { WorkOrderControllerService } from '@app/generated/api/';
import {
  RELEASE_TO_ALL,
  REVERT_RELEASE_TO_ALL,
  CLAIM,
  PUT_ON_HOLD,
  RELEASE_PUT_ON_HOLD,
  TOGGLE_FAVOURITE_WORK_ORDER,
} from 'src/app/actions';
import { WorkOrderObjectService } from './objects/work-order-object.service';
import { WorkOrderCommentsService } from './work-order-comments.service';
import { WorkOrderDocumentService } from './documents/work-order-document.service';
import { WorkOrderMaterialService } from './materials/work-order-material.service';
import { WorkOrderEquipmentService } from './overview/work-order-equipment.service';
import { AbstractWorkOrderDataService } from './abstract-work-order-data.service';
import { StatusCodesService } from './objects/status-codes.service';
import { WeightReportService } from './weight-report/weight-report.service';
import { v4 as uuid } from 'uuid';
import { firstValueFrom, ReplaySubject, Subject } from 'rxjs';
import { ClaimWorkOrder } from '@app/core/model/claim-work-order';
import { ReleaseToAll } from '@app/core/model/release-to-all';
import { ApiLoadErrorService } from '@app/core/services/api-load-error.service';
import { LoaderService } from '@app/header/loader/loader.service';
import { DocumentDownloadSelection, WorkOrderDownloadOptions } from '@app/core/model/document-download';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderDetailsService extends AbstractWorkOrderDataService<WorkOrderDetailsDto> implements OnDestroy {
  private outdated: boolean = false;
  private readonly destr$ = new Subject<void>();

  private isLoaded = false;
  public workOrder$ = new ReplaySubject<WorkOrderDetailsDto>(1);
  private usedOfflineFallback = false;
  private downloadOpsChain: Promise<void> = Promise.resolve();

  constructor(
    private workOrderController: WorkOrderControllerService,
    private loaderService: LoaderService,
    private apiLoadErrorService: ApiLoadErrorService,
    private weightReportService: WeightReportService,
    injector: Injector,
  ) {
    super(injector, 'WORK_ORDER', [
      WorkOrderEquipmentService,
      WorkOrderCommentsService,
      WorkOrderDocumentService,
      WorkOrderMaterialService,
      WorkOrderObjectService,
      StatusCodesService,
    ]);
    this.getData().subscribe((workOrderDetails) => {
      this.workOrder$.next(workOrderDetails);
    });
  }

  ngOnDestroy() {
    this.destr$.next();
    this.destr$.complete();
  }

  load(): void {
    this.workOrderController.getWorkOrder(this.workOrderId).subscribe({
      next: (data) => {
        this.setData(data);
        this.initChildren(this.workOrderId, data);
        this.usedOfflineFallback = false;
      },
      error: (error) => {
        console.warn(
          `[WORK_ORDER] Failed to load ${this.workOrderId} from backend. Falling back to offline data.`,
          error,
        );
        void this.handleBackendLoadFailure();
      },
    });
  }

  private async handleBackendLoadFailure(): Promise<void> {
    this.usedOfflineFallback = true;

    if (navigator.onLine && (await this.loaderService.confirmBackendReachable())) {
      this.apiLoadErrorService.report(
        'WORK_ORDER',
        'Failed to load work order details. Showing the downloaded copy when available.',
      );
    }

    this.rehydrate();
  }

  protected rehydrate(): void {
    this.usedOfflineFallback = true;
    this.offlineData.workOrderDataItems
      .where({ workOrderId: this.workOrderId, type: 'WORK_ORDER' })
      .first()
      .then((item) => {
        if (!item?.data) {
          return;
        }

        const workOrder = item.data as WorkOrderDetailsDto;
        this.usedOfflineFallback = true;
        this.setData(workOrder);
        this.initChildren(this.workOrderId, workOrder);
      })
      .catch((error) => {
        console.error('[WORK_ORDER] rehydrate error', error);
      });
  }

  workOrderLoaded() {
    return this.isLoaded;
  }

  didUseOfflineFallback(): boolean {
    return this.usedOfflineFallback;
  }

  async refresh(): Promise<void> {
    this.outdated = false;
    const workOrderId = this.workOrderId;
    this.initialize(workOrderId);
  }

  public setOutdated(value: boolean) {
    this.outdated = value;
  }

  public isOutdated() {
    return this.outdated;
  }

  async releaseToAll() {
    const event = { workOrderId: this.workOrderId } as ReleaseToAll;
    await this.changeLogService.publish(RELEASE_TO_ALL, event, [this.workOrderId]);
  }

  async revertReleaseToAll() {
    const event = { workOrderId: this.workOrderId };
    await this.changeLogService.publish(REVERT_RELEASE_TO_ALL, event, [this.workOrderId]);
  }

  async putOnHold() {
    const id: string = uuid();
    const event = { workOrderId: this.workOrderId, id };
    await this.changeLogService.publish(PUT_ON_HOLD, event, [this.workOrderId]);
  }

  async releasePutOnHold() {
    const event = { workOrderId: this.workOrderId };
    await this.changeLogService.publish(RELEASE_PUT_ON_HOLD, event, [this.workOrderId]);
  }

  async claim() {
    const event = { workOrderId: this.workOrderId } as ClaimWorkOrder;
    await this.changeLogService.publish(CLAIM, event, [this.workOrderId]);
  }

  async toggleFavouriteWorkOrder() {
    const event = { workOrderId: this.workOrderId };
    await this.changeLogService.publish(TOGGLE_FAVOURITE_WORK_ORDER, event, [this.workOrderId]);
  }

  // New stateless method
  async downloadWorkOrder(workOrderId: string, options?: WorkOrderDownloadOptions) {
    void this.requestPersistentStorage();
    return this.enqueueDownloadOp(async () => {
      const loadedWorkOrder = await firstValueFrom(this.workOrderController.getWorkOrder(workOrderId));

      // Set current context explicitly for the base download pipeline.
      this.workOrderId = workOrderId;
      this.setData(loadedWorkOrder);

      // Always (re)initialize children to guarantee the download chain includes every data type.
      this.initChildren(workOrderId, loadedWorkOrder);
      await super.download(options);

      await this.weightReportService.download(workOrderId);

      await this.offlineData.workOrders.put({
        workOrderId,
        workCenterId: loadedWorkOrder.workCenterId || '',
        projectId: loadedWorkOrder.projectId || '',
        lastUpdated: new Date(),
        documentSelection: options?.selection ?? null,
      });
    });
  }

  async getStoredDocumentSelection(workOrderId: string): Promise<DocumentDownloadSelection | null> {
    const item = await this.offlineData.workOrders.get(workOrderId);
    return item?.documentSelection ?? null;
  }

  // Without this, Android may evict downloaded work orders when the device runs low on storage.
  private async requestPersistentStorage(): Promise<void> {
    try {
      if (!navigator.storage?.persist || (await navigator.storage.persisted())) {
        return;
      }
      const granted = await navigator.storage.persist();
      console.info(`[WORK_ORDER] Persistent storage ${granted ? 'granted' : 'not granted'}`);
    } catch (error) {
      console.warn('[WORK_ORDER] Persistent storage request failed', error);
    }
  }

  // Keep original signature for compatibility (calls super)
  async download(options?: WorkOrderDownloadOptions): Promise<void> {
    return super.download(options);
  }

  async isWorkOrderDownloaded(workOrderId: string) {
    const result = await this.offlineData.workOrders.get(workOrderId);
    return !!result;
  }

  async removeDownloadWorkOrder(workOrderId: string) {
    return this.enqueueDownloadOp(async () => {
      const workOrderFilter = { workOrderId };
      await this.offlineData.workOrderFiles.where(workOrderFilter).delete();
      await this.offlineData.workOrderDataItems.where(workOrderFilter).delete();
      await this.offlineData.workOrders.where(workOrderFilter).delete();
    });
  }

  private enqueueDownloadOp<T>(task: () => Promise<T>): Promise<T> {
    const run = this.downloadOpsChain.then(task, task);
    this.downloadOpsChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  protected apply(workOrder: WorkOrderDetailsDto, event: ChangeLogEvent) {
    switch (event.type) {
      case TOGGLE_FAVOURITE_WORK_ORDER: {
        workOrder.isFavourite = !workOrder.isFavourite;
        break;
      }
      case RELEASE_TO_ALL: {
        workOrder.releasedToAll = true;
        break;
      }
      case REVERT_RELEASE_TO_ALL: {
        workOrder.releasedToAll = false;
        break;
      }
      case CLAIM: {
        workOrder.releasedToAll = false;
        workOrder.isAssigned = true;
        break;
      }
    }
  }
}
